import { People as PeopleIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  Alert,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ActionCard } from '@/components/dashboard/ActionCard';
import { AlertSummaryCard } from '@/components/dashboard/AlertSummaryCard';
import { FacilityTrendCard } from '@/components/dashboard/FacilityTrendCard';
import { RecentActivityCard } from '@/components/dashboard/RecentActivityCard';
import { SyncStatusBar } from '@/components/dashboard/SyncStatusBar';
import { getMe } from '@/lib/api/auth';
import { getDashboardSummary } from '@/lib/api/dashboard';
import { useHasRole } from '@/lib/hooks/useHasRole';
import { useAuthStore } from '@/store/authStore';

interface QuickLink {
  to: string;
  label: string;
  description: string;
  icon: typeof PeopleIcon;
  adminOnly?: boolean;
}

const QUICK_LINKS: QuickLink[] = [
  {
    to: '/residents',
    label: '입소자 관리',
    description: '호실별 입소자 점수·경보·메모 확인',
    icon: PeopleIcon,
  },
  {
    to: '/settings',
    label: '시스템 설정',
    description: '경보 임계값 등 운영 정책 관리',
    icon: SettingsIcon,
    adminOnly: true,
  },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const cached = useAuthStore((s) => s.staff);
  const setStaff = useAuthStore((s) => s.setStaff);
  const isAdmin = useHasRole('ADMIN');
  const canSeeTrend = useHasRole('ADMIN', 'MANAGER');

  const { data: staff } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const data = await getMe();
      setStaff(data);
      return data;
    },
    initialData: cached ?? undefined,
  });

  // 외래 카드는 QUICK_LINKS에서 아예 제외돼 있다 — FEATURES.outpatient 참고
  const visibleLinks = QUICK_LINKS.filter((l) => !l.adminOnly || isAdmin);

  // 출근 직후 현황 — 자정 집계·경보가 갱신되므로 화면 복귀 시 다시 가져온다.
  const summary = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: getDashboardSummary,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const s = summary.data;

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h4">
          {staff ? `${staff.nickname}님, 환영합니다.` : '대시보드'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {s ? `${s.date} 기준 오늘의 조치 대상입니다.` : '오늘도 안전한 하루 되세요.'}
        </Typography>
      </Stack>

      {summary.isError && (
        <Alert severity="error">현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</Alert>
      )}

      {summary.isLoading && (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={116} />
            </Grid>
          ))}
          <Grid size={{ xs: 12, md: 7 }}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        </Grid>
      )}

      {s && (
        <>
          {/* 조치 대상 — 클릭하면 입소자 목록의 해당 필터로 바로 진입 */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <ActionCard
                label="오늘 미사용"
                value={s.actions.unused}
                suffix={`/ ${s.actions.total}명`}
                hint="오늘 BGT 기록이 없는 입소자"
                tone="critical"
                to="/residents?filter=unused"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <ActionCard
                label="점수 급락"
                value={s.actions.dropped}
                suffix="명"
                hint="전일 대비 10% 이상 하락"
                tone="warning"
                to="/residents?filter=dropped"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <ActionCard
                label="오늘 BGT 사용률"
                value={s.bgt.rate}
                suffix={`% (${s.bgt.used}/${s.bgt.total})`}
                hint="입소중 입소자 기준"
                tone="info"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <ActionCard
                label="보이스웰니스 사용률"
                value={s.voiceWellness?.rate ?? '—'}
                suffix={
                  s.voiceWellness
                    ? `% (${s.voiceWellness.used}/${s.voiceWellness.total})`
                    : undefined
                }
                hint={s.sync.voicecheckConnected ? '입소중 입소자 기준' : '연동 확인 필요'}
                tone="info"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <AlertSummaryCard alerts={s.alerts} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <ActionCard
                label="보이스웰니스 계정 미연결"
                value={s.actions.voiceUnlinked}
                suffix={`/ ${s.actions.total}명`}
                hint="연결해야 3종 점수가 표시됩니다"
                tone="warning"
                to="/residents"
              />
            </Grid>
          </Grid>

          {/* 추이는 관리자 관점 지표라 권한자에게만 (직원 일상 동선에는 덜 중요) */}
          {canSeeTrend && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <FacilityTrendCard />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <RecentActivityCard />
              </Grid>
            </Grid>
          )}

          <SyncStatusBar sync={s.sync} />
        </>
      )}

      <Grid container spacing={2}>
        {visibleLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={link.to}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
                    borderColor: 'primary.light',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(link.to)}
                  sx={{ height: '100%', alignItems: 'stretch', p: 1 }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Stack
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: 'rgba(37, 99, 235, 0.10)',
                          color: 'primary.main',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon />
                      </Stack>
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {link.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {link.description}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {staff && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              세션 정보
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">{staff.username}</Typography>
              <Typography variant="body2" color="text.secondary">
                ·
              </Typography>
              <Typography variant="body2">{staff.role}</Typography>
              {staff.lastLoginAt && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    ·
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    마지막 로그인 {new Date(staff.lastLoginAt).toLocaleString('ko-KR')}
                  </Typography>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
